// Header.js
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import LanguageSwitch from "./LanguageSwitch";
import { User, LogOut, BookMarked, Palette, ChevronDown, AtSign, Coffee, UserCircle, Heart } from "lucide-react";
import AvatarDisplay from "./AvatarDisplay";
import { useProfile } from "../contexts/ProfileContext";
import SupportModal from "./SupportModal";

export default function Header({ onOpenUsernameModal, isTrusted }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user, login, logout, loading } = useAuth();
  const { profileAvatar } = useProfile();

  const [isVisible, setIsVisible] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const lastScrollY = useRef(typeof window !== "undefined" ? window.scrollY : 0);
  const ticking = useRef(false);
  const userMenuRef = useRef(null);

  const handleLogoClick = () => {
    // Navigate to home and clear any query params
    navigate('/');
  };

  const handleSignIn = async () => {
    await login();
  };

  const handleLogout = async () => {
    setShowUserMenu(false);
    await logout();
  };

  const handleMyCollection = () => {
    setShowUserMenu(false);
    navigate('/collection');
  };

  const handleMyPalettes = () => {
    setShowUserMenu(false);
    navigate('/palettes');
  };

  const handleMyProfile = () => {
    setShowUserMenu(false);
    navigate(`/profile/${user.username}`);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const threshold = 10; // ignore small jiggles
    const onScroll = () => {
      const currentY = window.scrollY || 0;
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          // only react if movement is meaningful
          if (Math.abs(currentY - lastScrollY.current) > threshold) {
            if (currentY > lastScrollY.current && currentY > 64) {
              // scrolling down -> hide
              setIsVisible(false);
            } else {
              // scrolling up -> show
              setIsVisible(true);
            }
            lastScrollY.current = currentY;
          }
          ticking.current = false;
        });
        ticking.current = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
    <header
      className={`bg-gradient-to-r from-pink-50 via-purple-50 to-pink-50 border-b border-pink-100 fixed left-0 right-0 z-50 transform transition-transform duration-300 ${
        isVisible ? "translate-y-0" : "-translate-y-full"
      }`}
    >
      <div className="max-w-4xl mx-auto px-4">
        <div className="relative flex items-center justify-center">
          {/* Logo + título - Centralizado */}
          <div 
            className="flex flex-col items-center gap-2 cursor-pointer hover:scale-105 transition-all duration-300"
            onClick={handleLogoClick}
            title={t("header.goHomeTitle")}
          >
            {/* Logo e texto lado a lado */}
            <div className="flex items-center justify-center gap-1">
              {/* Logo à esquerda */}
              <img 
                src="/images/markersync-logo-transparent.png" 
                alt="MarkerSync Logo" 
                className="w-20 h-20 rounded-2xl" 
              />
              {/* Texto à direita */}
              <div className="text-left">
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-pink-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  MarkerSync
                </h1>
                <p className="text-xs text-pink-500 font-medium">
                  {t("header.subtitle")}
                </p>
              </div>
            </div>
          </div>

          {/* Right side controls - Language switch + User menu */}
          <div className="absolute right-0 top-1/2 transform -translate-y-1/2 flex flex-col sm:flex-row items-end sm:items-center gap-1 sm:gap-3">
            {/* User Menu */}
            {!loading && (
              <div className="relative" ref={userMenuRef}>
                {!user ? (
                  // Sign In Button
                  <button
                    onClick={handleSignIn}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-white/50 rounded-lg transition-all"
                    title={t('auth.signIn')}
                  >
                    <User size={18} />
                    <span className="hidden sm:inline">{t('auth.signIn')}</span>
                  </button>
                ) : (
                  // Logged In User
                  <>
                    <button
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      className="flex items-center gap-2 py-1.5 text-sm font-medium text-slate-700 hover:bg-white/50 rounded-lg transition-all"
                    >
                      {isTrusted && profileAvatar !== null ? (
                        <AvatarDisplay avatar={profileAvatar} username={user.username} size="xs" />
                      ) : user.photoURL ? (
                        <img
                          src={user.photoURL}
                          alt={user.displayName || 'User'}
                          className="w-7 h-7 rounded-full border-2 border-white shadow-sm"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-400 to-purple-400 flex items-center justify-center text-white text-xs font-bold">
                          {user.displayName?.charAt(0) || user.email?.charAt(0) || '?'}
                        </div>
                      )}
                      <span className="hidden sm:inline max-w-[100px] truncate">
                        {user.displayName || user.email?.split('@')[0]}
                      </span>
                      <ChevronDown size={14} className={`transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown Menu */}
                    {showUserMenu && (
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50">
                        {/* User Info */}
                        <div className="px-4 py-2 border-b border-slate-100">
                          <p className="text-sm font-medium text-slate-800 truncate">
                            {user.displayName || 'User'}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {user.email}
                          </p>
                        </div>

                        {/* Menu Items */}
                        <button
                          onClick={handleMyCollection}
                          className="w-full px-4 py-2.5 text-left hover:bg-slate-50 flex items-center gap-3 text-sm text-slate-700"
                        >
                          <BookMarked size={16} />
                          {t('auth.myCollection')}
                        </button>

                        <button
                          onClick={handleMyPalettes}
                          className="w-full px-4 py-2.5 text-left hover:bg-slate-50 flex items-center gap-3 text-sm text-slate-700"
                        >
                          <Palette size={16} />
                          {t('savedPalettes.page.title')}
                        </button>

                        {isTrusted ? (
                          user.username && (
                            <button
                              onClick={handleMyProfile}
                              className="w-full px-4 py-2.5 text-left hover:bg-slate-50 flex items-center gap-3 text-sm text-slate-700"
                            >
                              <UserCircle size={16} />
                              My Profile
                            </button>
                          )
                        ) : (
                          <button
                            onClick={() => { setShowUserMenu(false); onOpenUsernameModal(); }}
                            className="w-full px-4 py-2.5 text-left hover:bg-slate-50 flex items-center gap-3 text-sm text-slate-700"
                          >
                            <AtSign size={16} />
                            {user.username ? `@${user.username}` : 'Set Username'}
                          </button>
                        )}

                        <div className="border-t border-slate-100 mt-1 pt-1">
                          <button
                            onClick={() => { setShowUserMenu(false); setShowSupportModal(true); }}
                            className="w-full px-4 py-2.5 text-left hover:bg-yellow-50 flex items-center gap-3 text-sm text-yellow-600"
                          >
                            <Heart size={16} />
                            Support me
                          </button>
                        </div>

                        <div className="border-t border-slate-100 mt-1 pt-1">
                          <button
                            onClick={handleLogout}
                            className="w-full px-4 py-2.5 text-left hover:bg-red-50 flex items-center gap-3 text-sm text-red-600"
                          >
                            <LogOut size={16} />
                            {t('auth.logOut')}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* <LanguageSwitch /> */}
          </div>
        </div>
      </div>
    </header>

    <SupportModal isOpen={showSupportModal} onClose={() => setShowSupportModal(false)} />
    </>
  );
}
